/* ============================================================
   TimelineView — date 속성 기준 가로 타임라인 (MVP)
   - 상단 날짜 축: 주 단위 눈금(일요일 시작), 가로 스크롤
   - 행마다 date 값 위치에 바(select 상태 색) 렌더, 클릭 → 행 페이지
   - date 컬럼이 없으면 안내, 여러 개면 사용할 컬럼 선택 드롭다운
   ============================================================ */
import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { optClass } from './viewPipeline';

const PX_DAY = 24; // 하루 = 24px

const parseIso = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const diffDays = (a, b) => Math.round((b - a) / 86400000);

export default function TimelineView({ columns, rows, view, onChangeDateKey }) {
  const dateCols = columns.filter((c) => c.type === 'date');
  const dateKey = view.config?.dateKey || dateCols[0]?.key;
  const statusCol = columns.find((c) => c.type === 'select');
  const scrollRef = useRef(null);

  // date 값이 있는 행만 (YYYY-MM-DD 로 정규화), 날짜순 정렬 유지
  const items = useMemo(
    () =>
      rows
        .map((r) => ({ row: r, iso: String(r.properties?.[dateKey] || '').slice(0, 10) }))
        .filter((it) => /^\d{4}-\d{2}-\d{2}$/.test(it.iso))
        .sort((a, b) => a.iso.localeCompare(b.iso)),
    [rows, dateKey]
  );

  // 축 범위: (최소 날짜 - 1주, 일요일 스냅) ~ (최대 날짜 + 2주)
  const range = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let min = today;
    let max = today;
    items.forEach((it) => {
      const d = parseIso(it.iso);
      if (d < min) min = d;
      if (d > max) max = d;
    });
    let start = addDays(min, -7);
    start = addDays(start, -start.getDay()); // 일요일 스냅
    const end = addDays(max, 14);
    return { start, days: diffDays(start, end) + 1, today };
  }, [items]);

  const ticks = useMemo(() => {
    const arr = [];
    for (let i = 0; i < range.days; i += 7) {
      const d = addDays(range.start, i);
      arr.push({ x: i * PX_DAY, label: `${d.getMonth() + 1}/${d.getDate()}` });
    }
    return arr;
  }, [range]);

  const todayX = diffDays(range.start, range.today) * PX_DAY;

  // 진입 시 오늘 근처로 스크롤
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = Math.max(0, todayX - el.clientWidth / 3);
  }, [dateKey, todayX]);

  if (!dateCols.length) {
    return (
      <div className="db-state">
        <div className="big">📆</div>
        <div>타임라인에 사용할 날짜(date) 속성이 필요합니다.</div>
        <div className="muted" style={{ fontSize: 12 }}>표 뷰에서 날짜 타입 속성을 추가하세요.</div>
      </div>
    );
  }

  const undated = rows.length - items.length;

  return (
    <div className="tl-view">
      <div className="tl-head">
        {dateCols.length > 1 && (
          <span className="grp-bar inline" style={{ marginLeft: 0 }}>
            <span className="grp-label">날짜 기준</span>
            <select
              className="grp-select"
              value={dateKey || ''}
              onChange={(e) => onChangeDateKey(e.target.value)}
            >
              {dateCols.map((c) => (
                <option key={c.key} value={c.key}>{c.name}</option>
              ))}
            </select>
          </span>
        )}
        {undated > 0 && (
          <span className="tl-hint">날짜 없는 행 {undated}건은 표시되지 않습니다.</span>
        )}
      </div>

      {items.length === 0 ? (
        <div className="db-state">
          <div className="big">📆</div>
          <div>표시할 날짜 값이 있는 행이 없습니다.</div>
        </div>
      ) : (
        <div className="tl-scroll" ref={scrollRef}>
          <div className="tl-canvas" style={{ width: range.days * PX_DAY }}>
            <div className="tl-axis">
              {ticks.map((t) => (
                <span className="tl-tick" key={t.x} style={{ left: t.x }}>{t.label}</span>
              ))}
            </div>
            <div className="tl-grid">
              {ticks.map((t) => (
                <span className="tl-gridline" key={t.x} style={{ left: t.x }} />
              ))}
              <span className="tl-today" style={{ left: todayX }} title="오늘" />
            </div>
            <div className="tl-lanes">
              {items.map(({ row, iso }) => {
                const x = diffDays(range.start, parseIso(iso)) * PX_DAY;
                const sv = statusCol ? row.properties?.[statusCol.key] : '';
                const cls = sv ? optClass(statusCol, sv) : 'tbl';
                return (
                  <div className="tl-lane" key={row.page_id}>
                    <Link
                      className={`tl-bar tag ${cls}`}
                      to={`/page/${row.page_id}`}
                      style={{ left: x }}
                      title={`${row.title || '제목 없음'} · ${iso}`}
                    >
                      {row.icon || '📄'} {row.title || '제목 없음'}
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
